import { lightenColor } from "./utils";
import type { TrackingDetails } from "./hooks";
import { BRAND_COLORS } from "../../../lib/brandColors";

export type ChartSeries = {
  id: string;
  label: string;
  color: string;
  samples: Array<{ date: Date; value: number }>;
  category: "sensor" | "expedition";
};

export type CombinedSample = {
  timestamp: number;
  [seriesId: string]: number | string;
};

export const SENSOR_COLORS = [
  BRAND_COLORS.orange,
  BRAND_COLORS.blueDark,
  BRAND_COLORS.blueLight,
  BRAND_COLORS.black,
];

export function axisFormatter(value: number) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fr-FR", { month: "2-digit", day: "2-digit" });
}

export function tooltipLabelFormatter(value: number) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
}

export function buildChartSeries(details: TrackingDetails): ChartSeries[] {
  const sensorSeries = details.sensors.map((sensor, index) => ({
    id: `sensor-${sensor.sensorId || index}`,
    label: sensor.sensorId ? `Capteur ${sensor.sensorId}` : `Capteur ${index + 1}`,
    color: SENSOR_COLORS[index % SENSOR_COLORS.length],
    samples: sensor.samples,
    category: "sensor" as const,
  }));

  const mapSensorToColor = new Map<string, string>();
  sensorSeries.forEach((serie) => {
    mapSensorToColor.set(serie.label.replace(/^Capteur\s/, ""), serie.color);
    mapSensorToColor.set(serie.id.replace(/^sensor-/, ""), serie.color);
  });

  const expeditionSeries = details.expeditions.map((expedition) => {
    const baseColor =
      mapSensorToColor.get(expedition.sensorId) ??
      SENSOR_COLORS[expedition.expeditionIndex % SENSOR_COLORS.length];
    return {
      id: `expedition-${expedition.expeditionIndex}-${expedition.sensorId}`,
      label: `Expé ${expedition.expeditionIndex + 1} · ${expedition.sensorId}`,
      color: lightenColor(baseColor, 0.55),
      samples: expedition.samples,
      category: "expedition" as const,
    };
  });

  return [...sensorSeries, ...expeditionSeries];
}

export function splitChartSeries(series: ChartSeries[]) {
  return {
    sensors: series.filter((item) => item.category === "sensor"),
    expeditions: series.filter((item) => item.category === "expedition"),
  };
}

export function mergeSamples(series: ChartSeries[]): CombinedSample[] {
  const lookup = new Map<number, CombinedSample>();

  series.forEach((serie) => {
    serie.samples.forEach((sample) => {
      const key = sample.date.getTime();
      if (!Number.isFinite(key)) return;
      const existing = lookup.get(key);
      if (existing) {
        existing[serie.id] = sample.value;
      } else {
        lookup.set(key, {
          timestamp: key,
          [serie.id]: sample.value,
        });
      }
    });
  });

  return Array.from(lookup.values()).sort((a, b) => a.timestamp - b.timestamp);
}

export function computeSeriesDomain(series: ChartSeries[]) {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  series.forEach((serie) => {
    serie.samples.forEach((sample) => {
      if (Number.isFinite(sample.value)) {
        min = Math.min(min, sample.value);
        max = Math.max(max, sample.value);
      }
    });
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return undefined;
  }

  const spread = Math.max(max - min, 1);
  const padding = Math.max(spread * 0.1, 0.5);

  return [Math.floor(min - padding), Math.ceil(max + padding)] as const;
}
